/**
 * AG-UI streaming client for the DMI AI Analyst.
 *
 * The endpoint is a POST that returns server-sent events, so EventSource
 * cannot be used and the body is read and framed here.
 */
import { ApiError, readCsrfToken } from "./api";
import { readAnswerContext, type AnswerContext } from "./answerContext";
import { readAnswerReceipt, type AnswerReceipt } from "./answerReceipt";
// Type-only, so it is erased at compile time. `types.ts` imports from this module
// the same way, and neither import survives to run, so the pair cannot form a
// cycle.
import type { ArtifactSection } from "../types";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
const AG_UI_PATH = import.meta.env.VITE_AG_UI_PATH ?? `${BASE_URL}/ag-ui`;

/**
 * Which entry action opened a turn.
 *
 * Dashboard, report, and artifact actions start their own guided interviews. A
 * typed message is `analyze`, which keeps the existing behavior including the
 * single-chart fast path.
 */
export type AgentIntent = "analyze" | "dashboard" | "report" | "artifact";

export interface AgentTurn {
  threadId: string;
  runId: string;
  message: string;
  workflowStep: string;
  workspaceId?: string;
  /** The governed contract this turn should run against. */
  contextId?: string;
  contextVersion?: string;
  /** Set only when the user has confirmed moving the thread to a new contract. */
  repinContext?: boolean;
  /** Defaults to `analyze` server-side when omitted. */
  intent?: AgentIntent;
  /**
   * Plan this turn without the earlier answers. Only "new" can be asked for:
   * whether a turn refines the previous answer is the planner's decision.
   */
  relationHint?: "new";
}

/**
 * A dashboard the agent composed and the API saved on the way through.
 *
 * Carries the panel specification rather than a link to another system, which
 * is what lets the client render it, refresh it, and export it. `saved` is
 * false when the specification arrived but persisting it failed: the charts
 * are still renderable, they are simply not in the workspace, and the UI must
 * say so rather than offering to open something that is not there.
 */
export interface DashboardComposedEvent {
  dashboardId: string;
  title: string;
  saved: boolean;
  saveError?: string;
  spec: {
    dashboardId?: string;
    title: string;
    contextId: string;
    contextVersion: string | null;
    panels: {
      id?: string;
      title: string;
      chartType: string;
      metrics: string[];
      dimensions: string[];
      encoding: { x: string; y: string[]; series: string };
      narrative: string;
    }[];
    previews: {
      panelIndex: number;
      rows: Record<string, unknown>[];
      rowCount: number;
      complete: boolean;
      /** Metric member to its certified unit. */
      units?: Record<string, string>;
      endorsement?: string | null;
      periodNotes?: string[];
    }[];
  };
}

export interface AgentStepEvent {
  summary: string;
  actor: string;
  /** routing, delegating, perceiving, planning, validating, executing, observing, rendering, accounting. */
  phase: string;
  /** The architectural layer the step reached into. */
  layer: string;
  /** The concrete endpoint that was called, when there was one. */
  service?: string;
  tool?: string;
  status: "started" | "success" | "error";
  /** What the step observed: ids, row counts, governed figures, real errors. */
  detail?: Record<string, unknown>;
}

export interface QuestionOption {
  id: string;
  label: string;
  description?: string;
}

/**
 * A structured question that pauses the conversation for a decision.
 *
 * The run that delivers a card finishes normally, so the card is held by the UI
 * rather than by an open stream. `answerStep` is echoed back with the answer,
 * which is what lets the runtime reject a card answered after a reload instead
 * of overwriting a newer choice.
 */
export interface ClarifyingQuestionEvent {
  questionId: string;
  summary: string;
  answerStep: string;
  options: QuestionOption[];
  /** Pager position, when the question is one of a known sequence. */
  position?: number;
  total?: number;
  freeText: boolean;
  freeTextLabel: string;
  skippable: boolean;
}

export interface FollowUpSuggestion {
  id: string;
  prompt: string;
  workflowStep?: string;
}

/**
 * An invitation to keep this answer as a gold query on the governed contract.
 *
 * A context pack cannot be published without one, and the cheapest moment to
 * obtain a good one is while somebody is reading a figure they asked for and
 * trust. It arrives as an offer only: accepting is a separate call the user makes
 * under their own identity, and the answer is re-read by the context layer, so no
 * figure is certified on the strength of what the browser happened to be showing.
 */
export interface GoldQueryOffer {
  contextId: string;
  contextVersion: string;
  /** A suggested phrasing. The approver may reword it before accepting. */
  question: string;
  semanticQuery: {
    metrics: string[];
    dimensions: string[];
    limit?: number;
  };
  rowCount?: number;
}

/**
 * A governed document the agent composed, and the relay saved on the way past.
 *
 * ``version`` is why this event carries more than the dashboard's does. A
 * document is meant to be sent, so a change to one produces the next version
 * rather than a replacement, and the client needs to know which version it is
 * showing to label it and to fetch the right one later.
 */
export interface ArtifactComposedEvent {
  artifactId: string;
  title: string;
  saved: boolean;
  saveError?: string;
  version: number;
  /** Set when this event revised a document instead of creating one. */
  revisionOf?: string;
  /**
   * Only carried when the document was read back out of the workspace. A
   * freshly composed one is always a draft, so the reader assumes that; a
   * stored one may already have been published and must not offer to publish
   * it again.
   */
  status?: "draft" | "published";
  spec: {
    artifactId?: string;
    title: string;
    contextId: string;
    contextVersion: string | null;
    version?: number;
    revisionOf?: string;
    sections: ArtifactSection[];
    previews?: {
      sectionIndex: number;
      rows: Record<string, unknown>[];
      rowCount: number;
      complete: boolean;
      /** Metric member to its certified unit. */
      units?: Record<string, string>;
      endorsement?: string | null;
      periodNotes?: string[];
    }[];
  };
}

export interface AgentHandlers {
  /** A fragment of the assistant's reply. Fragments arrive in order. */
  onText?: (delta: string) => void;
  /** A safe activity summary. Never private chain-of-thought. */
  onTrace?: (summary: string, actor: string) => void;
  /** One structured step in the pipeline, naming the layer and service. */
  onStep?: (step: AgentStepEvent) => void;
  /** A governed dashboard was composed, saved, and is ready to render. */
  onDashboardComposed?: (event: DashboardComposedEvent) => void;
  /** A governed document was composed or revised, and is ready to render. */
  onArtifactComposed?: (event: ArtifactComposedEvent) => void;
  /** This answer can be kept as a gold query, if the user wants to. */
  onGoldQueryOffered?: (offer: GoldQueryOffer) => void;
  /** The agent needs a decision before it can continue. */
  onQuestion?: (question: ClarifyingQuestionEvent) => void;
  /** Prompts offered once an answer is complete. */
  onFollowUps?: (suggestions: FollowUpSuggestion[]) => void;
  /** How this answer was understood, and what changed from the previous one. */
  onAnswerContext?: (context: AnswerContext) => void;
  /** What the answer's figures were computed from, and whether each checks out. */
  onAnswerReceipt?: (receipt: AnswerReceipt) => void;
  /** Whether the thread is pinned to a source it can query. */
  onSourceReadiness?: (ready: boolean) => void;
  onFinished?: () => void;
  onError?: (
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) => void;
}

export interface AgUiEvent {
  type?: string;
  name?: string;
  delta?: string;
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
  value?: {
    summary?: string;
    actor?: string;
    // DASHBOARD_COMPOSED fields
    dashboardId?: string;
    saved?: boolean;
    saveError?: string;
    spec?: DashboardComposedEvent["spec"] | ArtifactComposedEvent["spec"];
    // ARTIFACT_COMPOSED fields
    artifactId?: string;
    version?: number;
    // GOLD_QUERY_OFFERED fields
    offer?: GoldQueryOffer;
    // AGENT_STEP fields
    phase?: string;
    layer?: string;
    service?: string;
    tool?: string;
    status?: string;
    detail?: Record<string, unknown>;
    // CLARIFYING_QUESTION fields
    questionId?: string;
    answerStep?: string;
    options?: QuestionOption[];
    position?: number;
    total?: number;
    freeText?: boolean;
    freeTextLabel?: string;
    skippable?: boolean;
    // FOLLOWUP_SUGGESTIONS fields
    suggestions?: FollowUpSuggestion[];
    // SOURCE_READINESS fields
    ready?: boolean;
  };
}

/** Splits a byte stream into complete server-sent event frames. */
export function parseFrames(buffer: string): {
  events: string[];
  rest: string;
} {
  const parts = buffer.split("\n\n");
  // The final part may be an incomplete frame, so it is carried forward.
  const rest = parts.pop() ?? "";
  return { events: parts, rest };
}

/** Extracts the JSON payload from one frame, ignoring id and comment lines. */
export function readFrame(frame: string): AgUiEvent | null {
  const data = frame
    .split("\n")
    .filter((line) => line.startsWith("data: "))
    .map((line) => line.slice(6))
    .join("");
  if (!data) return null;
  try {
    return JSON.parse(data) as AgUiEvent;
  } catch {
    return null;
  }
}

export function dispatch(event: AgUiEvent, handlers: AgentHandlers): void {
  if (event.type === "TEXT_MESSAGE_CONTENT" && event.delta) {
    handlers.onText?.(event.delta);
    return;
  }
  if (event.type === "CUSTOM" && event.name === "SAFE_REASONING_TRACE") {
    const summary = event.value?.summary;
    if (summary) handlers.onTrace?.(summary, event.value?.actor ?? "Agent");
    return;
  }
  if (event.type === "CUSTOM" && event.name === "AGENT_STEP") {
    const v = event.value;
    if (v?.summary) {
      handlers.onStep?.({
        summary: v.summary,
        actor: v.actor ?? "Agent",
        phase: v.phase ?? "executing",
        layer: v.layer ?? "",
        service: v.service,
        tool: v.tool,
        status: v.status === "error" || v.status === "success" ? v.status : "started",
        detail: v.detail,
      });
    }
    return;
  }
  if (event.type === "CUSTOM" && event.name === "DASHBOARD_COMPOSED") {
    const v = event.value;
    const spec = v?.spec as DashboardComposedEvent["spec"] | undefined;
    // A specification with no panels is not a dashboard, and rendering the
    // empty shell reads as a broken feature rather than an empty result.
    if (spec && spec.panels?.length) {
      handlers.onDashboardComposed?.({
        dashboardId: v?.dashboardId ?? "",
        title: spec.title ?? "",
        saved: v?.saved ?? false,
        saveError: v?.saveError,
        spec,
      });
    }
    return;
  }
  if (event.type === "CUSTOM" && event.name === "GOLD_QUERY_OFFERED") {
    const offer = event.value?.offer;
    // An offer with no replayable query cannot be accepted, and a button that
    // fails when pressed is worse than no button.
    if (offer?.contextId && offer.semanticQuery?.metrics?.length) {
      handlers.onGoldQueryOffered?.(offer);
    }
    return;
  }
  if (event.type === "CUSTOM" && event.name === "ARTIFACT_COMPOSED") {
    const v = event.value;
    const spec = v?.spec as ArtifactComposedEvent["spec"] | undefined;
    // A document with no sections is not a document. Dropped for the same reason
    // an empty dashboard is: an empty shell reads as a broken feature.
    if (spec && spec.sections?.length) {
      handlers.onArtifactComposed?.({
        artifactId: v?.artifactId ?? spec.artifactId ?? "",
        title: spec.title ?? "",
        saved: v?.saved ?? false,
        saveError: v?.saveError,
        // Defaults to 1 so an unsaved document still labels itself coherently
        // rather than showing "version 0".
        version: v?.version ?? spec.version ?? 1,
        revisionOf: spec.revisionOf,
        spec,
      });
    }
    return;
  }
  if (event.type === "CUSTOM" && event.name === "CLARIFYING_QUESTION") {
    const v = event.value;
    // A card with neither options nor a free-text row cannot be answered, so
    // showing it would strand the conversation. Dropped rather than rendered.
    const options = Array.isArray(v?.options) ? v.options : [];
    if (v?.summary && v.questionId && v.answerStep && (options.length || v.freeText)) {
      handlers.onQuestion?.({
        questionId: v.questionId,
        summary: v.summary,
        answerStep: v.answerStep,
        options,
        position: v.position,
        total: v.total,
        freeText: v.freeText ?? false,
        freeTextLabel: v.freeTextLabel ?? "Something else",
        skippable: v.skippable ?? false,
      });
    }
    return;
  }
  if (event.type === "CUSTOM" && event.name === "FOLLOWUP_SUGGESTIONS") {
    const suggestions = event.value?.suggestions;
    if (Array.isArray(suggestions) && suggestions.length) {
      handlers.onFollowUps?.(
        suggestions.filter((item) => item?.id && item?.prompt),
      );
    }
    return;
  }
  if (event.type === "CUSTOM" && event.name === "ANSWER_CONTEXT") {
    const context = readAnswerContext(event.value);
    if (context) handlers.onAnswerContext?.(context);
    return;
  }
  if (event.type === "CUSTOM" && event.name === "ANSWER_RECEIPT") {
    const receipt = readAnswerReceipt(event.value);
    if (receipt) handlers.onAnswerReceipt?.(receipt);
    return;
  }
  if (event.type === "CUSTOM" && event.name === "SOURCE_READINESS") {
    handlers.onSourceReadiness?.(event.value?.ready ?? false);
    return;
  }
  if (event.type === "RUN_ERROR") {
    handlers.onError?.(
      event.code ?? "agent_error",
      event.message ?? "The analyst could not complete this request.",
      // Details name the current context version or the available contexts,
      // which is what lets the page offer an action rather than a dead end.
      event.details ?? {},
    );
    return;
  }
  if (event.type === "RUN_FINISHED") {
    handlers.onFinished?.();
  }
}

export async function streamAgentTurn(
  turn: AgentTurn,
  handlers: AgentHandlers,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(AG_UI_PATH, {
      method: "POST",
      credentials: "include",
      signal,
      headers: {
        "content-type": "application/json",
        "x-csrf-token": readCsrfToken(),
      },
      body: JSON.stringify({
        threadId: turn.threadId,
        runId: turn.runId,
        messages: [{ role: "user", content: turn.message }],
        state: {
          workspaceId: turn.workspaceId ?? "private",
          workflowStep: turn.workflowStep,
          intent: turn.intent ?? "analyze",
          contextId: turn.contextId ?? "",
          contextVersion: turn.contextVersion ?? "",
          repinContext: turn.repinContext ?? false,
          ...(turn.relationHint ? { relationHint: turn.relationHint } : {}),
        },
        forwardedProps: {},
      }),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
    throw new ApiError(0, {
      code: "network_unavailable",
      message: "The analyst service could not be reached.",
      retryable: true,
    });
  }

  if (!response.ok || !response.body) {
    throw new ApiError(response.status, {
      code: response.status === 401 ? "unauthenticated" : "agent_unavailable",
      message: "The analyst service rejected the request.",
    });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = parseFrames(buffer);
      buffer = rest;
      for (const frame of events) {
        const event = readFrame(frame);
        if (event) dispatch(event, handlers);
      }
    }
  } catch (error) {
    if (!(error instanceof DOMException && error.name === "AbortError")) {
      throw error;
    }
  } finally {
    reader.releaseLock();
  }
}
